import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from '@Sage/hoi-poi-ui';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { CopilotContext } from '@Sage/ssm-web5';
import { SALESORDERS } from 'constants/Entities';
import Context from 'managers/Context';
import SalesOrdersBottomInfoPanel, {
    useSalesOrdersBottomInfoPanelHeight,
} from '../components/SalesOrdersBottomInfoPanel';
import { getActiveCrud } from 'utils/crud';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';

const useSalesOrdersCrud = () => {
    const [panelWidth, setPanelWidth] = useState(SALESORDERS.crudPanelWidth);
    const dispatch = useDispatch();
    const {
        activeCrud,
        crudPermissions,
        permission,
        salesOrderTypeFilter,
        useTaxesSalesOrders,
        salesOrderShippingCost,
        showSalesOrderFooterAmounts,
    } = useSelector((state) => ({
        activeCrud: getActiveCrud(state),
        crudPermissions:
            state.config?.permission?.crud_permission?.[SALESORDERS.permission] || null,
        permission: state.config?.permission || {},
        salesOrderTypeFilter: state.entityFilters?.[SALESORDERS.entity]?.filters?.salesOrderType,
        useTaxesSalesOrders: state?.config?.userData?.useTaxesSalesOrders || false,
        salesOrderShippingCost: state?.config?.userData?.salesOrderShippingCost === true,
        showSalesOrderFooterAmounts:
            state.config?.userData?.showSalesOrderFooterAmounts == null ||
            getBackendBoolean(state.config.userData.showSalesOrderFooterAmounts),
    }));
    const bottomInfoPanelHeight = useSalesOrdersBottomInfoPanelHeight();
    const { copilotBarProps, setCopilotBarProps } = useContext(CopilotContext);
    const { isOpen: isCopilotBarOpen } = copilotBarProps;

    const handleResize = useCallback(() => {
        let width = window.innerWidth - 350;
        if (width > SALESORDERS.crudPanelWidth) width = SALESORDERS.crudPanelWidth;
        setPanelWidth(width);
        if (isCopilotBarOpen && setCopilotBarProps) {
            setCopilotBarProps({ width });
        }
    }, [isCopilotBarOpen, setCopilotBarProps]);

    useEffect(() => {
        if (activeCrud.entity !== SALESORDERS.entity) return;
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeCrud.entity, handleResize]);

    const getCanDelete = useCallback(() => {
        const id = activeCrud?.id || null;
        return id && crudPermissions && crudPermissions.delete;
    }, [activeCrud, crudPermissions]);

    const changeList = useCallback(
        (salesOrderType) => {
            dispatch(Context.actions.ServerListActions.getList('tblTiposSalesOrders')).then(
                (data) => {
                    const item = data.find((item) => String(item.value) === salesOrderType);
                    dispatch(
                        Context.actions.EntityFiltersActions.changeFilter({
                            entity: SALESORDERS,
                            filter: {
                                id: 'salesOrderType',
                                noCacheCheck: true,
                                hideForCount: true,
                            },
                            value: [item],
                        }),
                    );
                },
            );
        },
        [dispatch],
    );

    const onSave = useCallback(
        (result, data) => {
            let toastLiteral = 'succes_entitycreatedsuccessfully';
            if (result?.Updated?.num === '1') toastLiteral = 'succes_entityupdatedsuccessfully';
            let text = getLiteral(toastLiteral);
            const salesOrderType = String(salesOrderTypeFilter?.value?.[0]?.value);
            const dataSalesOrderType = String(data.salesOrderType?.value || data.salesOrderType);
            if (salesOrderType !== dataSalesOrderType && dataSalesOrderType)
                text = (
                    <div>
                        {getLiteral(text)}
                        <br />
                        <br />
                        <Link
                            onClick={() => changeList(dataSalesOrderType)}
                            underline
                            type="caption"
                            className="fm-toast-view"
                        >
                            {getLiteral('label_notification_see_salesorders')}
                        </Link>
                    </div>
                );
            successToast({
                text,
            });
        },
        [changeList, salesOrderTypeFilter],
    );

    const onErrorSave = useCallback((data) => {
        let toastLiteral = 'label_failed_create';
        if (data.id) toastLiteral = 'label_failed_update';
        errorToast({
            text: getLiteral(toastLiteral),
        });
    }, []);

    const getInfo = useCallback(
        (data) => {
            if (!data?.companyWarning) return null;
            if (!permission.warnings) return null;
            if (!permission.editWarnings && !permission.viewWarningsInSalesOrders) return null;
            return data.companyWarning;
        },
        [permission.editWarnings, permission.viewWarningsInSalesOrders, permission.warnings],
    );

    const bottomPanel = useMemo(() => {
        return {
            className: 'fm-sales-orders-bottom-panel',
            height: bottomInfoPanelHeight,
            render: ({ data }) => {
                if (activeCrud.entity !== SALESORDERS.entity) return null;
                if (!showSalesOrderFooterAmounts) return null;

                const idBranch = data?.idBranch?.value ?? data?.idBranch;
                if (!idBranch) return null;

                return (
                    <SalesOrdersBottomInfoPanel
                        data={data}
                        showTaxDetails={useTaxesSalesOrders}
                        showShippingCost={salesOrderShippingCost}
                    />
                );
            },
        };
    }, [
        activeCrud.entity,
        bottomInfoPanelHeight,
        salesOrderShippingCost,
        showSalesOrderFooterAmounts,
        useTaxesSalesOrders,
    ]);

    return {
        bottomPanel,
        entity: SALESORDERS,
        width: panelWidth,
        className: 'fm-sales-orders-crud',
        literalNewEntity: 'title_add_salesorder',
        literalEditEntity: 'label_editing_salesorders',
        getNewEntityLiteral: null,
        getEditEntityLiteral: null,
        onSave,
        onErrorSave,
        onDelete: undefined,
        onErrorDelete: undefined,
        hasCrudInDetail: true,
        getCanDelete,
        deleteProps: undefined,
        literalsBulk: undefined,
        getInfo,
    };
};

export default useSalesOrdersCrud;
